import React from "react";
import {Helmet} from "react-helmet";
import useSiteSettings from "~utils/useSiteSettings";

const SITE_URL = "https://misura.com.au";
const SHOWROOMS_PATH = "/showrooms/";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const formatTelephone = (value) => String(value).replace(/\s+/g, "");

const slugify = (value) =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Expands a free-text day label into schema.org day names.
 * Handles "Monday - Friday" (a range) and "Saturday" (a single day).
 * Returns null when the label cannot be understood, so the caller can drop the
 * row rather than publish a guess.
 */
const parseDays = (label) => {
  const found = DAYS.filter((day) => new RegExp(day, "i").test(String(label)));

  if (found.length === 0) return null;
  if (found.length === 1) return found;

  // Two named days with a dash between them means an inclusive range.
  if (found.length === 2 && /-|–|to/i.test(String(label))) {
    const [from, to] = found.map((day) => DAYS.indexOf(day)).sort((a, b) => a - b);

    return DAYS.slice(from, to + 1);
  }

  return found;
};

/**
 * Converts one side of an "9.30 - 5.00" range into 24-hour "HH:MM".
 *
 * The CMS field is free text with no am/pm, so the hour is disambiguated by
 * retail convention: 8–11 is morning, 1–7 is afternoon. That covers every value
 * currently stored. Anything outside those bounds returns null so the row is
 * skipped instead of emitting a wrong time.
 */
const parseTime = (value) => {
  const match = String(value)
    .trim()
    .match(/^(\d{1,2})[.:]?(\d{2})?$/);

  if (!match) return null;

  const minutes = Number(match[2] ?? 0);
  let hours = Number(match[1]);

  if (minutes > 59) return null;
  if (hours >= 1 && hours <= 7)
    hours += 12; // afternoon
  else if (hours === 12) hours = 12;
  else if (hours < 8 || hours > 23) return null;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const toOpeningHoursSpecification = (rows) =>
  (rows ?? [])
    .map((row) => {
      const dayOfWeek = parseDays(row?.days);
      const [open, close] = String(row?.hours ?? "").split(/-|–/);
      const opens = parseTime(open);
      const closes = parseTime(close);

      if (!dayOfWeek || !opens || !closes) return null;

      return {"@type": "OpeningHoursSpecification", dayOfWeek, opens, closes};
    })
    .filter(Boolean);

/**
 * Per-showroom LocalBusiness JSON-LD for the showrooms page.
 *
 * Only showrooms with a street address are emitted: Google requires `address`
 * on a LocalBusiness, and publishing one without it — or for a location that
 * has closed — would assert a storefront that isn't there. The Sydney showroom
 * is currently closed and has no address in Sanity, so it is skipped
 * automatically and will reappear once its new address is entered.
 */
const LocalBusinessSchema = () => {
  const {showrooms} = useSiteSettings();

  const businesses = (showrooms ?? [])
    .filter((showroom) => showroom?.address)
    .map((showroom) => {
      const openingHoursSpecification = toOpeningHoursSpecification(showroom.openingHours);

      return {
        "@context": "https://schema.org",
        "@type": "FurnitureStore",
        "@id": `${SITE_URL}${SHOWROOMS_PATH}#${slugify(showroom.title)}`,
        name: `MISURA — ${showroom.title}`,
        url: `${SITE_URL}${SHOWROOMS_PATH}`,
        parentOrganization: {"@id": `${SITE_URL}/#organization`},
        address: {
          "@type": "PostalAddress",
          streetAddress: showroom.address,
          ...(showroom.city && {addressLocality: showroom.city}),
          ...(showroom.state && {addressRegion: showroom.state}),
          ...(showroom.postCode && {postalCode: showroom.postCode}),
          addressCountry: "AU",
        },
        ...(typeof showroom.lat === "number" &&
          typeof showroom.lng === "number" && {
            geo: {
              "@type": "GeoCoordinates",
              latitude: showroom.lat,
              longitude: showroom.lng,
            },
          }),
        ...(showroom.phoneNumber && {telephone: formatTelephone(showroom.phoneNumber)}),
        ...(showroom.mapUrl && {hasMap: showroom.mapUrl}),
        ...(openingHoursSpecification.length > 0 && {openingHoursSpecification}),
      };
    });

  if (businesses.length === 0) return null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(businesses)}</script>
    </Helmet>
  );
};

export default LocalBusinessSchema;
